import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { persistenceService } from '../utils/persistenceService';

interface User {
  id: string;
  email: string;
  name: string;
  onboardingStatus: 'complete' | 'in_progress';
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state from persistence service
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = persistenceService.getAuthToken();
        const storedUser = persistenceService.getUserProfile();
        
        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(storedUser);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        // Clear corrupted data
        persistenceService.removeAuthToken();
        persistenceService.removeUserProfile();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = (newToken: string, userData: User) => {
    setToken(newToken);
    setUser(userData);
    persistenceService.setAuthToken(newToken);
    persistenceService.setUserProfile(userData);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    persistenceService.clearAllData();
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      persistenceService.setUserProfile(updatedUser);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token,
    isLoading,
    login,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 